// Workflows drawn on the_Kit's canvas: versions draft -> live -> retired, one live at a time (SPEC §7). A version
// is stored as the kit's WorkflowDocument (nodes + edges). Two kinds (C-F6): a campaign workflow (9 node types, run
// once per person of a campaign) and a classification workflow (7 node types, run on new data: it only adds tags
// and puts people in batches). The node packs and the run engines share the lists below.
import { z } from 'zod'
import { WorkflowDocument } from '@the-kit/contracts'
import { Id, IsoDate } from './common'

export const WorkflowKind = z.enum(['campaign', 'classification'])
export type WorkflowKind = z.infer<typeof WorkflowKind>

export const NodeType = z.enum([
  'trigger.audience',
  'campaign.ivr_call',
  'campaign.wait_window',
  'campaign.http_preset',
  'campaign.condition',
  'campaign.whatsapp',
  'campaign.sms',
  'campaign.event',
  'campaign.end',
  'trigger.new_data',
  'classify.field_check',
  'classify.list_check',
  'classify.score',
  'classify.add_tag',
  'classify.put_in_batch',
  'classify.end',
])
export type NodeType = z.infer<typeof NodeType>

/** The node types each kind may use (its palette); the first one is its only trigger. */
export const KIND_NODES: Record<WorkflowKind, readonly NodeType[]> = {
  campaign: NodeType.options.filter((t) => t === 'trigger.audience' || t.startsWith('campaign.')),
  classification: NodeType.options.filter((t) => t === 'trigger.new_data' || t.startsWith('classify.')),
}

/**
 * The ports (edge "when" words) each node type can leave by. The lender API node's ports depend on its route's kind
 * (lenderApiPorts): a dedupe leaves by pass / fail, a prefill by link / no_link (C-F5: the lender gave the person a
 * link or not, R3), the other kinds by next. A missing port means next.
 */
export const NODE_PORTS: Record<NodeType, readonly string[]> = {
  'trigger.audience': ['next'],
  'campaign.ivr_call': ['yes', 'stop', 'no_answer'],
  'campaign.wait_window': ['next'],
  'campaign.http_preset': ['pass', 'fail', 'next', 'link', 'no_link'],
  'campaign.condition': ['yes', 'no'],
  'campaign.whatsapp': ['next'],
  'campaign.sms': ['next'],
  'campaign.event': ['next'],
  'campaign.end': [],
  'trigger.new_data': ['next'],
  'classify.field_check': ['yes', 'no'],
  'classify.list_check': ['yes', 'no'],
  'classify.score': ['yes', 'no'],
  'classify.add_tag': ['next'],
  'classify.put_in_batch': ['next'],
  'classify.end': [],
}

export { WorkflowDocument }

export const VersionStatus = z.enum(['draft', 'live', 'retired'])
export type VersionStatus = z.infer<typeof VersionStatus>

export const WorkflowVersion = z.object({
  id: Id,
  workflow_id: Id,
  version: z.number().int().min(1),
  status: VersionStatus,
  published_at: IsoDate.nullable(),
  published_by_name: z.string().nullable(),
  document: WorkflowDocument,
})
export type WorkflowVersion = z.infer<typeof WorkflowVersion>

/** active = runs; paused = kept but not run (no sorting, no new campaign); archived = put away, read only, hidden. */
export const WorkflowState = z.enum(['active', 'paused', 'archived'])
export type WorkflowState = z.infer<typeof WorkflowState>

export const WorkflowSummary = z.object({
  id: Id,
  name: z.string(),
  kind: WorkflowKind,
  live_version: z
    .object({ version: z.number().int(), published_at: IsoDate, published_by_name: z.string() })
    .nullable(),
  drafts: z.number().int().min(0),
  used_by: z
    .array(z.string())
    .describe('campaign workflow: the campaigns that run it; classification: the batches it fills'),
  state: WorkflowState,
})
export type WorkflowSummary = z.infer<typeof WorkflowSummary>

export const WorkflowDetail = z.object({
  id: Id,
  name: z.string(),
  kind: WorkflowKind,
  state: WorkflowState,
  versions: z.array(WorkflowVersion).describe('newest first'),
})
export type WorkflowDetail = z.infer<typeof WorkflowDetail>

export const AddWorkflowNodeInput = z.object({
  type: NodeType,
  name: z.string().trim().min(1, 'Give the node a label'),
  after: Id.describe('the node the new one goes after'),
  port: z.string().min(1).describe('the port of "after" to put it on, e.g. next or yes'),
  cfg: z.record(z.string(), z.unknown()),
})
export type AddWorkflowNodeInput = z.infer<typeof AddWorkflowNodeInput>

export const CreateWorkflowInput = z.object({
  name: z.string().trim().min(3, 'Give it a name people understand'),
  kind: WorkflowKind,
})
export type CreateWorkflowInput = z.infer<typeof CreateWorkflowInput>

/** Pause, resume, archive or restore: active <-> paused, active or paused -> archived, archived -> paused. */
export const WorkflowStateInput = z.object({ state: WorkflowState })
export type WorkflowStateInput = z.infer<typeof WorkflowStateInput>

export interface WorkflowsApi {
  listWorkflows(): Promise<WorkflowSummary[]>
  /** a new workflow with a v1 draft: its kind's trigger -> end */
  createWorkflow(input: CreateWorkflowInput): Promise<WorkflowSummary>
  getWorkflow(id: string): Promise<WorkflowDetail>
  /** copies the live version (or the newest) into a new draft */
  newWorkflowDraft(workflowId: string): Promise<WorkflowVersion>
  saveWorkflowDraft(versionId: string, document: WorkflowDocument): Promise<WorkflowVersion>
  addWorkflowNode(versionId: string, input: AddWorkflowNodeInput): Promise<WorkflowVersion>
  /** the draft goes live, the old live version is retired; refused while the checks fail */
  publishWorkflowVersion(versionId: string): Promise<WorkflowVersion>
  /** pause, resume, archive or restore; archiving is refused while an open campaign uses it */
  setWorkflowState(workflowId: string, input: WorkflowStateInput): Promise<WorkflowSummary>
  /** only a workflow that never went live; one that did is archived instead */
  deleteWorkflow(workflowId: string): Promise<void>
  /** removes a draft that is not the workflow's only version */
  discardWorkflowDraft(versionId: string): Promise<void>
}
